import { createStore } from 'zustand';

import {
  AllSearchResults,
  PartnerExamsCoverage,
  ProgressiveSearchModalKeys,
  RecentSearches,
  SearchResult,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { SearchType } from '@/types/most-search-type';

export interface SearchState {
  isSearchModalOpen: boolean;
  searchTerm?: string;
  selectedFilter?: string;
  fetchedResults?: AllSearchResults;
  recentSearches?: RecentSearches[];
  selectedSearch?: SearchResult;
  pendingModalKey?: ProgressiveSearchModalKeys;
  pendingSelectedProcedures?: SelectedProcedures[];
  examsCoverage?: PartnerExamsCoverage;
}

export const useSearchState = createStore<SearchState>(() => ({
  isSearchModalOpen: false,
  searchTerm: undefined,
  selectedFilter: undefined,
  fetchedResults: undefined,
  recentSearches: undefined,
  selectedSearch: undefined,
  pendingModalKey: undefined,
  pendingSelectedProcedures: undefined,
  examsCoverage: undefined,
}));

export const setSelectedFilter = (filter: string | undefined) => {
  useSearchState.setState((state) => ({ ...state, selectedFilter: filter }));
};

export const setSelectedSearch = async (search: SearchResult) => {
  useSearchState.setState((state) => ({ ...state, selectedSearch: search }));
};

export const removeSelectedSearch = async () => {
  useSearchState.setState((state) => ({ ...state, selectedSearch: undefined }));
};

export const clearSearchState = async () => {
  useSearchState.setState((state) => ({
    ...state,
    searchTerm: undefined,
    selectedFilter: undefined,
    fetchedResults: undefined,
  }));
};

export const setFetchedResults = (results?: AllSearchResults) => {
  useSearchState.setState((state) => ({ ...state, fetchedResults: results }));
};

export const setSearchTerm = (term: string | undefined) => {
  useSearchState.setState((state) => ({ ...state, searchTerm: term }));
};

export function setFetchedResultsByType(type: string, results: SearchResult[], total: number) {
  useSearchState.setState((state) => {
    if (state.fetchedResults) {
      return {
        fetchedResults: {
          ...state.fetchedResults,
          [type]: {
            results,
            total,
          },
        },
      };
    }
    return {};
  });
}

export function appendRecentSearches(
  search: SearchResult,
  setStoredRecentSearches: (searches: SearchResult[],
  ) => void): SearchResult[] {
  const currentState = useSearchState.getState();
  const storedRecentSearches = currentState.recentSearches || [];

  const updatedRecentSearches = [
    search,
    ...storedRecentSearches.filter(
      (s) => s.id !== search.id || s.displayName !== search.displayName || s.searchType !== search.searchType,
    ),
  ].slice(0, 5);

  useSearchState.setState(() => ({
    recentSearches: updatedRecentSearches,
  }));

  setStoredRecentSearches(updatedRecentSearches);
  return updatedRecentSearches;
}

export function appendRecentSearchesList(searches: SearchResult[] = []) {
  let newRecentSearches: SearchResult[] = [];

  useSearchState.setState((state) => {
    const recentSearches = state.recentSearches || [];
    newRecentSearches = [...searches, ...recentSearches].slice(0, 5);
    return { recentSearches: newRecentSearches };
  });
}

export async function clearRecentSearches(setStoredRecentSearches: (searches: SearchResult[]) => void) {
  setStoredRecentSearches([]);
  useSearchState.setState({ recentSearches: [] });
}

export const isVaccineSearch = (): boolean => {
  return useSearchState.getState().selectedSearch?.searchType === SearchType.VACCINE;
};

export const openSearchModal = (open: boolean, searchTerm?: string) => {
  useSearchState.setState((state) => ({ ...state, isSearchModalOpen: open, searchTerm: open ? searchTerm : '' }));
};

export const initializeRecentSearches = (searches: SearchResult[]) => {
  useSearchState.setState((state) => ({ ...state, recentSearches: searches }));
};

export const openSearchModalAtStep = (
  key: ProgressiveSearchModalKeys,
  selectedProcedures: SelectedProcedures[],
): void => {
  useSearchState.setState((state) => ({
    ...state,
    isSearchModalOpen: true,
    pendingModalKey: key,
    pendingSelectedProcedures: selectedProcedures,
  }));
};

export const openSearchModalWithExamsCoverage = (
  coverage: PartnerExamsCoverage,
  selectedProcedures: SelectedProcedures[],
): void => {
  useSearchState.setState((state) => ({
    ...state,
    isSearchModalOpen: true,
    pendingModalKey: ProgressiveSearchModalKeys.EXAMS_COVERAGE,
    pendingSelectedProcedures: selectedProcedures,
    examsCoverage: coverage,
  }));
};

export const clearPendingModalSeed = (): void => {
  useSearchState.setState((state) => ({ ...state, pendingModalKey: undefined, pendingSelectedProcedures: undefined }));
};

export const clearExamsCoverage = (): void => {
  if (useSearchState.getState().examsCoverage === undefined) {
    return;
  }

  useSearchState.setState((state) => ({ ...state, examsCoverage: undefined }));
};
